import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  Form,
  Button,
  Table,
} from "react-bootstrap";
import { unstable_batchedUpdates } from "react-dom";
import { Link } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { Styles, CourseModalStyles } from "./styles/adminCourses.js";
import { useAuth } from "../../context/AuthContext";
import {
  ADMIN_COURSES_ENDPOINT,
  ADMIN_COURSES_UPDATE_ENDPOINT,
  ADMIN_USERS_ENDPOINT,
  APPLICATIONS_ENDPOINT,
  INTAKES_ENDPOINT,
  ADMIN_AGENTS_ENDPOINT,
} from "../../config/api";

const filterFieldOptions = [
  { value: "programName", label: "Program Name", type: "string" },
  { value: "universityName", label: "University Name", type: "string" },
  { value: "levelOfStudy", label: "Level of Study", type: "string" },
  { value: "location", label: "Location", type: "string" },
  { value: "duration", label: "Duration", type: "string" },
  { value: "concentration", label: "Concentration", type: "string" },
  { value: "currency", label: "Currency", type: "string" },
  { value: "intakes", label: "Intakes", type: "string" },
  { value: "deadlines", label: "Deadlines", type: "string" },
  { value: "tuitionFeePerYear", label: "Tuition Fee / Year", type: "number" },
  {
    value: "tuitionFeePerSemester",
    label: "Tuition Fee / Semester",
    type: "number",
  },
  {
    value: "tuitionFeePerCourse",
    label: "Tuition Fee / Course",
    type: "number",
  },
  { value: "applicationFee", label: "Application Fee", type: "number" },
  { value: "admissionFee", label: "Admission Fee", type: "number" },
  {
    value: "fifteenYearDegreeAccepted",
    label: "15 Year Degree Accepted",
    type: "boolean",
  },
  { value: "moiAccepted", label: "MOI Accepted", type: "boolean" },
  { value: "wesRequired", label: "WES Required", type: "boolean" },
];

const filterOperators = [
  { value: "equals", label: "Equal", types: ["string", "number", "boolean"] },
  {
    value: "notEquals",
    label: "Not equal",
    types: ["string", "number", "boolean"],
  },
  { value: "contains", label: "Contains", types: ["string"] },
  { value: "greaterThan", label: "Greater than", types: ["number"] },
  { value: "lessThan", label: "Less than", types: ["number"] },
];

const getOperatorsForType = (type) =>
  filterOperators.filter((operator) => operator.types.includes(type));

const generateFilterId = () =>
  `filter-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const createFilterTemplate = (fieldValue) => {
  const fallbackField = filterFieldOptions[0]?.value || "";
  const selectedField =
    fieldValue &&
    filterFieldOptions.some((option) => option.value === fieldValue)
      ? fieldValue
      : fallbackField;
  const fieldMeta = filterFieldOptions.find(
    (option) => option.value === selectedField
  );
  const availableOperators = fieldMeta
    ? getOperatorsForType(fieldMeta.type)
    : filterOperators;

  return {
    id: generateFilterId(),
    field: selectedField,
    operator: availableOperators[0]?.value || "",
    value: "",
  };
};

const findFilterFieldMeta = (fieldValue) =>
  filterFieldOptions.find((option) => option.value === fieldValue) || null;

const MAX_PROGRAM_DETAILS_FILE_BYTES = 2 * 1024 * 1024;

const FULL_HTML_DOCUMENT_PATTERN =
  /<!doctype\s+html|<\/?html[\s>]|<\/?head[\s>]|<\/?body[\s>]|<script[\s>]|<style[\s>]/i;

const looksLikeFullHtmlDocument = (html) =>
  typeof html === "string" && FULL_HTML_DOCUMENT_PATTERN.test(html);

const initialForm = {
  sourceId: "",
  universityName: "",
  programName: "",
  programDetails: "",
  levelOfStudy: "",
  location: "",
  duration: "",
  currency: "",
  tuitionFeePerYear: "",
  tuitionFeePerSemester: "",
  tuitionFeePerCourse: "",
  credits: "",
  perCreditRate: "",
  gpaOutOf4: "",
  applicationFee: "",
  admissionFee: "",
  scholarship: "",
  concentration: "",
  portalOrDirect: "",
  privateOrPublic: "",
  campus: "",
  standardizedTests: "",
  ielts: "",
  ieltsNblt: "",
  toefl: "",
  toeflNblt: "",
  duolingo: "",
  duolingoNblt: "",
  pte: "",
  pteNblt: "",
  backlogs: "",
  fifteenYearDegreeAccepted: "",
  moiAccepted: "",
  wesRequired: "",
  intakes: "",
  deadlines: "",
};

const applyModalInitialState = {
  show: false,
  course: null,
  loadingUsers: false,
  loadingIntakes: false,
  loadingAgents: false,
  users: [],
  intakes: [],
  agents: [],
  searchInput: "",
  userSearch: "",
  agentSearchInput: "",
  selectedUserId: "",
  selectedUser: null,
  selectedIntakeId: "",
  selectedIntake: null,
  selectedAgentId: "",
  selectedAgent: null,
  message: "",
  submitting: false,
  error: "",
  success: "",
};

const getUserId = (user) => {
  if (!user || typeof user !== "object") {
    return "";
  }
  return user.id || user._id || "";
};

const getIntakeId = (intake) => {
  if (!intake || typeof intake !== "object") {
    return "";
  }
  return intake.id || intake._id || "";
};

const formatIntakeLabel = (intake) => {
  if (!intake) {
    return "";
  }
  const name = intake.name || "Unnamed Intake";
  const year = intake.year ? ` ${intake.year}` : "";
  const start = intake.startDate
    ? new Date(intake.startDate).toLocaleDateString()
    : "";
  const end = intake.endDate
    ? new Date(intake.endDate).toLocaleDateString()
    : "";
  const range =
    start && end
      ? ` (${start} - ${end})`
      : start
      ? ` (starts ${start})`
      : "";
  return `${name}${year}${range}`.trim();
};

const getAgentId = (agent) => {
  if (!agent || typeof agent !== "object") {
    return "";
  }
  return agent.id || agent._id || "";
};

const formatAgentLabel = (agent) => {
  if (!agent) {
    return "";
  }
  const name = agent.name || "Unnamed Agent";
  const email = agent.email ? ` <${agent.email}>` : "";
  const phone = agent.phone ? ` • ${agent.phone}` : "";
  return `${name}${email}${phone}`.trim();
};

const getTuitionLabel = (course) => {
  if (!course) {
    return "";
  }
  const tuitionValue =
    course.tuitionFeePerYear ??
    course.tuitionFeePerSemester ??
    course.tuitionFeePerCourse;
  if (
    tuitionValue === undefined ||
    tuitionValue === null ||
    tuitionValue === ""
  ) {
    return "";
  }
  const currency = course.currency || "";
  const numeric = Number(tuitionValue);
  if (Number.isNaN(numeric)) {
    return "";
  }
  const formatted = numeric.toLocaleString();
  return `${currency} ${formatted}`.trim();
};

const parseNumberInput = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  const raw = typeof value === "string" ? value.trim() : value;
  if (raw === "") {
    return "";
  }
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : "";
};

const parseBooleanInput = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  if (typeof value === "boolean") {
    return value;
  }
  const normalized = String(value).trim().toLowerCase();
  if (["true", "yes", "1"].includes(normalized)) {
    return true;
  }
  if (["false", "no", "0"].includes(normalized)) {
    return false;
  }
  return "";
};

const AdminCourses = () => {
  const { user, loading: authLoading } = useAuth();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [modalState, setModalState] = useState({
    show: false,
    mode: "add",
    course: null,
    form: { ...initialForm },
    saving: false,
    error: "",
  });
  const [programDetailsMode, setProgramDetailsMode] = useState("visual");
  const [programDetailsFileError, setProgramDetailsFileError] = useState("");
  const programDetailsFileInputRef = useRef(null);
  const [applyModal, setApplyModal] = useState(applyModalInitialState);
  const [filtersState, setFiltersState] = useState(() =>
    filterFieldOptions.length ? [createFilterTemplate()] : []
  );
  const [appliedFilters, setAppliedFilters] = useState([]);
  const filterOptionsAvailable = filterFieldOptions.length > 0;
  const [exporting, setExporting] = useState(false);
  const [importStatus, setImportStatus] = useState({
    processing: false,
    success: "",
    error: "",
    errors: [],
  });
  const importFileInputRef = useRef(null);

  const isAdmin = useMemo(() => {
    if (!Array.isArray(user?.roles)) {
      return false;
    }
    return user.roles.some(
      (role) => typeof role === "string" && role.toLowerCase() === "admin"
    );
  }, [user]);

  const fetchCourses = useCallback(async () => {
    if (!isAdmin) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }
      if (appliedFilters.length) {
        params.set("filters", JSON.stringify(appliedFilters));
      }

      const response = await fetch(
        `${ADMIN_COURSES_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load courses.");
      }

      const payload = await response.json();
      setCourses(Array.isArray(payload.courses) ? payload.courses : []);
      if (payload.pagination) {
        setPagination({
          page: payload.pagination.page || page,
          limit: payload.pagination.limit || pageSize,
          totalItems: payload.pagination.totalItems || 0,
          totalPages: payload.pagination.totalPages || 1,
        });
        if (payload.pagination.page && payload.pagination.page !== page) {
          setPage(payload.pagination.page);
        }
      } else {
        setPagination({
          page,
          limit: pageSize,
          totalItems: payload.courses?.length || 0,
          totalPages: 1,
        });
      }
    } catch (err) {
      setError(err.message || "Unable to load courses.");
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, pageSize, searchTerm, appliedFilters]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchCourses();
    }
  }, [authLoading, isAdmin, fetchCourses]);

  const loadApplyUsers = useCallback(
    async (search = "") => {
      if (!isAdmin) {
        return;
      }

      setApplyModal((prev) => ({
        ...prev,
        loadingUsers: true,
        error: "",
        success: "",
      }));

      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("Authentication required.");
        }

        const params = new URLSearchParams({ limit: "50" });
        if (search) {
          params.set("search", search);
        }

        const response = await fetch(
          `${ADMIN_USERS_ENDPOINT}?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to load users.");
        }

        const payload = await response.json();
        const users = Array.isArray(payload.users) ? payload.users : [];

        setApplyModal((prev) => {
          const selectedIsPresent = users.some(
            (candidate) => getUserId(candidate) === prev.selectedUserId
          );
          const nextSelectedUser = selectedIsPresent
            ? users.find(
                (candidate) => getUserId(candidate) === prev.selectedUserId
              )
            : null;
          return {
            ...prev,
            loadingUsers: false,
            users,
            userSearch: search,
            error: "",
            selectedUserId: selectedIsPresent
              ? getUserId(nextSelectedUser)
              : "",
            selectedUser: selectedIsPresent ? nextSelectedUser : null,
          };
        });
      } catch (err) {
        setApplyModal((prev) => ({
          ...prev,
          loadingUsers: false,
          error: err.message || "Unable to load users.",
        }));
      }
    },
    [isAdmin]
  );

  const loadIntakes = useCallback(async () => {
    setApplyModal((prev) => ({
      ...prev,
      loadingIntakes: true,
      error: "",
      success: "",
    }));

    try {
      const response = await fetch(INTAKES_ENDPOINT);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load intakes.");
      }

      const payload = await response.json();
      const intakes = Array.isArray(payload.intakes) ? payload.intakes : [];

      setApplyModal((prev) => {
        const selectedExists = intakes.some(
          (candidate) => getIntakeId(candidate) === prev.selectedIntakeId
        );
        const nextSelectedIntake = selectedExists
          ? intakes.find(
              (candidate) => getIntakeId(candidate) === prev.selectedIntakeId
            )
          : null;
        return {
          ...prev,
          loadingIntakes: false,
          intakes,
          selectedIntakeId: selectedExists
            ? getIntakeId(nextSelectedIntake)
            : "",
          selectedIntake: selectedExists ? nextSelectedIntake : null,
        };
      });
    } catch (err) {
      setApplyModal((prev) => ({
        ...prev,
        loadingIntakes: false,
        error: err.message || "Unable to load intakes.",
      }));
    }
  }, []);

  const loadAgents = useCallback(async () => {
    if (!isAdmin) {
      setApplyModal((prev) => ({
        ...prev,
        loadingAgents: false,
      }));
      return;
    }

    setApplyModal((prev) => ({
      ...prev,
      loadingAgents: true,
      error: "",
      success: "",
    }));

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const params = new URLSearchParams({ limit: "200", status: "active" });
      const response = await fetch(
        `${ADMIN_AGENTS_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load agents.");
      }

      const payload = await response.json();
      const agents = Array.isArray(payload.agents) ? payload.agents : [];

      setApplyModal((prev) => {
        const selectedExists = agents.some(
          (candidate) => getAgentId(candidate) === prev.selectedAgentId
        );
        const nextSelectedAgent = selectedExists
          ? agents.find(
              (candidate) => getAgentId(candidate) === prev.selectedAgentId
            )
          : null;
        return {
          ...prev,
          loadingAgents: false,
          agents,
          selectedAgentId: selectedExists
            ? getAgentId(nextSelectedAgent)
            : "",
          selectedAgent: selectedExists ? nextSelectedAgent : null,
        };
      });
    } catch (err) {
      setApplyModal((prev) => ({
        ...prev,
        loadingAgents: false,
        error: err.message || "Unable to load agents.",
      }));
    }
  }, [isAdmin]);

  const openApplyModal = (course) => {
    setApplyModal({
      ...applyModalInitialState,
      show: true,
      course,
    });
    loadApplyUsers("");
    loadIntakes();
    loadAgents();
  };

  const closeApplyModal = () => {
    setApplyModal(applyModalInitialState);
  };

  const triggerApplyUserSearch = useCallback(
    (query) => {
      const trimmed = query.trim();
      setApplyModal((prev) => ({
        ...prev,
        searchInput: trimmed,
      }));
      loadApplyUsers(trimmed);
    },
    [loadApplyUsers]
  );

  const handleApplyUserSearchChange = (event) => {
    const { value } = event.target;
    setApplyModal((prev) => ({
      ...prev,
      searchInput: value,
    }));
  };

  const handleApplyUserSearchSubmit = (event) => {
    event.preventDefault();
    triggerApplyUserSearch(applyModal.searchInput);
  };

  const handleApplyUserSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      triggerApplyUserSearch(applyModal.searchInput);
    }
  };

  const handleApplyUserSelect = (event) => {
    const { value } = event.target;
    setApplyModal((prev) => {
      const selectedUser =
        prev.users.find((user) => getUserId(user) === value) || null;
      return {
        ...prev,
        selectedUserId: value,
        selectedUser,
        error: "",
      };
    });
  };

  const handleApplyAgentSearchChange = (event) => {
    const { value } = event.target;
    setApplyModal((prev) => ({
      ...prev,
      agentSearchInput: value,
    }));
  };

  const handleApplyAgentSelect = (event) => {
    const { value } = event.target;
    setApplyModal((prev) => {
      const selectedAgent =
        prev.agents.find((agent) => getAgentId(agent) === value) || null;
      return {
        ...prev,
        selectedAgentId: value,
        selectedAgent,
      };
    });
  };

  const handleApplyIntakeSelect = (event) => {
    const { value } = event.target;
    setApplyModal((prev) => {
      const selectedIntake =
        prev.intakes.find((intake) => getIntakeId(intake) === value) || null;
      return {
        ...prev,
        selectedIntakeId: value,
        selectedIntake,
        error: "",
      };
    });
  };

  const handleApplyMessageChange = (event) => {
    const { value } = event.target;
    setApplyModal((prev) => ({
      ...prev,
      message: value,
    }));
  };

  const handleCreateApplication = async (event) => {
    event.preventDefault();
    if (!applyModal.course) {
      setApplyModal((prev) => ({
        ...prev,
        error: "Course details unavailable.",
      }));
      return;
    }
    if (!applyModal.selectedUser) {
      setApplyModal((prev) => ({
        ...prev,
        error: "Select a user to create the application.",
      }));
      return;
    }
    if (!applyModal.selectedIntake) {
      setApplyModal((prev) => ({
        ...prev,
        error: "Select an intake to create the application.",
      }));
      return;
    }

    setApplyModal((prev) => ({
      ...prev,
      submitting: true,
      error: "",
      success: "",
    }));

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const { course, selectedUser, selectedIntake, selectedAgent, message } =
        applyModal;
      const applicantName =
        `${selectedUser.firstName || ""} ${
          selectedUser.lastName || ""
        }`.trim() || selectedUser.email;

      const payload = {
        courseId: course._id,
        intakeId: getIntakeId(selectedIntake),
        applicantName,
        applicantEmail: selectedUser.email,
        applicantPhone: selectedUser.phone || "",
        message: message.trim(),
      };

      if (selectedAgent) {
        payload.agentId = getAgentId(selectedAgent);
      }

      const response = await fetch(APPLICATIONS_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to create application.");
      }

      setApplyModal((prev) => ({
        ...prev,
        submitting: false,
        success: "Application created successfully.",
      }));

      setTimeout(() => {
        closeApplyModal();
      }, 1200);
    } catch (err) {
      setApplyModal((prev) => ({
        ...prev,
        submitting: false,
        error: err.message || "Unable to create application.",
      }));
    }
  };

  const tableRows = useMemo(
    () =>
      courses.map((course) => {
        const tuitionLabel = getTuitionLabel(course) || "Tuition on request";
        return {
          id: course._id,
          programName: course.programName || "Untitled Program",
          universityName: course.universityName || "University",
          levelOfStudy: course.levelOfStudy || "",
          location: course.location || "",
          tuitionLabel,
          updatedAt: course.updatedAt
            ? new Date(course.updatedAt).toLocaleString()
            : "N/A",
          raw: course,
        };
      }),
    [courses]
  );

  const filteredAgents = useMemo(() => {
    const query = applyModal.agentSearchInput.trim().toLowerCase();
    if (!query) {
      return applyModal.agents;
    }
    return applyModal.agents.filter((agent) => {
      const haystack = [
        agent.name,
        agent.email,
        agent.phone,
        agent.address,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [applyModal.agentSearchInput, applyModal.agents]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.trim());
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    if (searchTerm) {
      setSearchTerm("");
      setPage(1);
    }
  };

  const handleExport = async () => {
    if (!isAdmin || exporting) {
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("Authentication required.");
      return;
    }

    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }
      if (appliedFilters.length) {
        params.set("filters", JSON.stringify(appliedFilters));
      }
      const totalRecords =
        pagination.totalItems || tableRows.length || pageSize;
      const limit = Math.max(Math.min(totalRecords, 5000), 1);
      params.set("limit", String(limit));

      const response = await fetch(
        `${ADMIN_COURSES_ENDPOINT}/export?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to export courses.");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = url;
      link.download = `courses-export-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      window.setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 0);
    } catch (err) {
      setError(err.message || "Unable to export courses.");
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    if (importStatus.processing) {
      return;
    }
    if (importFileInputRef.current) {
      importFileInputRef.current.click();
    }
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    const token = localStorage.getItem("authToken");
    if (!token) {
      setImportStatus({
        processing: false,
        success: "",
        error: "Authentication required.",
        errors: [],
      });
      event.target.value = "";
      return;
    }

    setImportStatus({
      processing: true,
      success: "",
      error: "",
      errors: [],
    });

    try {
      const text = await file.text();
      if (!text.trim()) {
        throw new Error("The selected file is empty.");
      }

      const response = await fetch(`${ADMIN_COURSES_ENDPOINT}/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fileName: file.name,
          content: text,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const errorDetails = Array.isArray(payload.errors)
          ? payload.errors
          : [];
        const message = payload.message || "Unable to import courses.";
        const extra = errorDetails.length
          ? ` (${errorDetails.length} issue${
              errorDetails.length === 1 ? "" : "s"
            } reported)`
          : "";
        const error = new Error(`${message}${extra}`);
        error.details = errorDetails;
        throw error;
      }

      const wasPageOne = page === 1;
      setPage(1);
      if (wasPageOne) {
        fetchCourses();
      }

      setImportStatus({
        processing: false,
        success: payload.message || "Courses imported successfully.",
        error: "",
        errors: Array.isArray(payload.errors) ? payload.errors : [],
      });
    } catch (err) {
      const errorDetails = Array.isArray(err?.details) ? err.details : [];
      setImportStatus({
        processing: false,
        success: "",
        error: err.message || "Unable to import courses.",
        errors: errorDetails,
      });
    } finally {
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const addFilterRow = () => {
    if (!filterOptionsAvailable) {
      return;
    }
    setFiltersState((prev) => {
      const lastField = prev[prev.length - 1]?.field;
      return [...prev, createFilterTemplate(lastField)];
    });
  };

  const removeFilterRow = (id) => {
    setFiltersState((prev) => {
      const next = prev.filter((filter) => filter.id !== id);
      if (next.length > 0) {
        return next;
      }
      return filterFieldOptions.length ? [createFilterTemplate()] : [];
    });
  };

  const handleFilterFieldChange = (id, fieldValue) => {
    const newMeta = findFilterFieldMeta(fieldValue);
    setFiltersState((prev) =>
      prev.map((filter) => {
        if (filter.id !== id) {
          return filter;
        }
        const currentMeta = findFilterFieldMeta(filter.field);
        const availableOperators = newMeta
          ? getOperatorsForType(newMeta.type)
          : filterOperators;
        const operator = availableOperators.some(
          (option) => option.value === filter.operator
        )
          ? filter.operator
          : availableOperators[0]?.value || "";
        const shouldResetValue =
          !currentMeta || !newMeta || currentMeta.type !== newMeta.type;
        return {
          ...filter,
          field: fieldValue,
          operator,
          value: shouldResetValue ? "" : filter.value,
        };
      })
    );
  };

  const handleFilterOperatorChange = (id, operatorValue) => {
    setFiltersState((prev) =>
      prev.map((filter) =>
        filter.id === id ? { ...filter, operator: operatorValue } : filter
      )
    );
  };

  const handleFilterValueChange = (id, value) => {
    setFiltersState((prev) =>
      prev.map((filter) => (filter.id === id ? { ...filter, value } : filter))
    );
  };

  const buildFiltersPayload = useCallback(
    () =>
      filtersState
        .map((filter) => {
          const meta = findFilterFieldMeta(filter.field);
          if (!meta) {
            return null;
          }
          const availableOperators = getOperatorsForType(meta.type);
          const operator = availableOperators.some(
            (option) => option.value === filter.operator
          )
            ? filter.operator
            : availableOperators[0]?.value || "";
          if (!operator) {
            return null;
          }

          if (meta.type === "string") {
            const trimmed =
              typeof filter.value === "string" ? filter.value.trim() : "";
            if (!trimmed) {
              return null;
            }
            return {
              field: filter.field,
              operator,
              value: trimmed,
            };
          }

          if (meta.type === "number") {
            const numeric = Number(filter.value);
            if (!Number.isFinite(numeric)) {
              return null;
            }
            return {
              field: filter.field,
              operator,
              value: numeric,
            };
          }

          if (meta.type === "boolean") {
            const normalized = String(filter.value ?? "")
              .trim()
              .toLowerCase();
            if (
              !["true", "false", "1", "0", "yes", "no"].includes(normalized)
            ) {
              return null;
            }
            const boolValue = ["true", "1", "yes"].includes(normalized);
            return {
              field: filter.field,
              operator,
              value: boolValue,
            };
          }

          return null;
        })
        .filter(Boolean),
    [filtersState]
  );

  const preparedFilters = useMemo(
    () => buildFiltersPayload(),
    [buildFiltersPayload]
  );
  const preparedFiltersString = useMemo(
    () => JSON.stringify(preparedFilters),
    [preparedFilters]
  );
  const appliedFiltersString = useMemo(
    () => JSON.stringify(appliedFilters),
    [appliedFilters]
  );
  const filtersChanged = preparedFiltersString !== appliedFiltersString;
  const hasAppliedFilters = appliedFilters.length > 0;

  const applyFiltersFromState = () => {
    if (filtersChanged) {
      setAppliedFilters(preparedFilters);
      setPage(1);
    }
  };

  const clearFilters = () => {
    const hasFiltersApplied = appliedFilters.length > 0;
    setFiltersState(filterFieldOptions.length ? [createFilterTemplate()] : []);
    if (hasFiltersApplied) {
      setAppliedFilters([]);
      setPage(1);
    }
  };

  const openModal = (mode, course = null) => {
    setProgramDetailsMode(
      mode === "edit" && course && looksLikeFullHtmlDocument(course.programDetails)
        ? "html"
        : "visual"
    );
    if (mode === "edit" && course) {
      setModalState({
        show: true,
        mode,
        course,
        form: {
          sourceId: course.sourceId || "",
          universityName: course.universityName || "",
          programName: course.programName || "",
          programDetails: course.programDetails || "",
          levelOfStudy: course.levelOfStudy || "",
          location: course.location || "",
          duration: course.duration || "",
          currency: course.currency || "",
          tuitionFeePerYear: course.tuitionFeePerYear ?? "",
          tuitionFeePerSemester: course.tuitionFeePerSemester ?? "",
          tuitionFeePerCourse: course.tuitionFeePerCourse ?? "",
          credits: course.credits ?? "",
          perCreditRate: course.perCreditRate ?? "",
          gpaOutOf4: course.gpaOutOf4 ?? "",
          applicationFee: course.applicationFee ?? "",
          admissionFee: course.admissionFee ?? "",
          scholarship: course.scholarship || "",
          concentration: course.concentration || "",
          portalOrDirect: course.portalOrDirect || "",
          privateOrPublic: course.privateOrPublic || "",
          campus: course.campus || "",
          standardizedTests: course.standardizedTests || "",
          ielts: course.ielts ?? "",
          ieltsNblt: course.ieltsNblt ?? "",
          toefl: course.toefl ?? "",
          toeflNblt: course.toeflNblt ?? "",
          duolingo: course.duolingo ?? "",
          duolingoNblt: course.duolingoNblt ?? "",
          pte: course.pte ?? "",
          pteNblt: course.pteNblt ?? "",
          backlogs: course.backlogs || "",
          fifteenYearDegreeAccepted:
            typeof course.fifteenYearDegreeAccepted === "boolean"
              ? String(course.fifteenYearDegreeAccepted)
              : "",
          moiAccepted:
            typeof course.moiAccepted === "boolean"
              ? String(course.moiAccepted)
              : "",
          wesRequired:
            typeof course.wesRequired === "boolean"
              ? String(course.wesRequired)
              : "",
          intakes: course.intakes || "",
          deadlines: course.deadlines || "",
        },
        saving: false,
        error: "",
      });
    } else {
      setModalState({
        show: true,
        mode: "add",
        course: null,
        form: { ...initialForm },
        saving: false,
        error: "",
      });
    }
  };

  const closeModal = () => {
    setProgramDetailsMode("visual");
    setModalState({
      show: false,
      mode: "add",
      course: null,
      form: { ...initialForm },
      saving: false,
      error: "",
    });
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: value,
      },
      error: "",
    }));
  };

  const handleProgramDetailsChange = (value) => {
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        programDetails: value,
      },
      error: "",
    }));
  };

  const handleToggleProgramDetailsMode = () => {
    setProgramDetailsMode((current) => {
      if (current === "visual") {
        return "html";
      }
      if (
        looksLikeFullHtmlDocument(modalState.form.programDetails) &&
        !window.confirm(
          "This looks like a full HTML page (with <script>/<style> tags). " +
            "Switching to the Visual Editor may strip scripts, styles, or markup it doesn't support. Continue?"
        )
      ) {
        return current;
      }
      return "visual";
    });
  };

  const handleProgramDetailsFileButtonClick = () => {
    setProgramDetailsFileError("");
    if (programDetailsFileInputRef.current) {
      programDetailsFileInputRef.current.value = "";
      programDetailsFileInputRef.current.click();
    }
  };

  const handleProgramDetailsFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    const isHtmlFile =
      /\.html?$/i.test(file.name) ||
      file.type === "text/html" ||
      file.type === "";

    if (!isHtmlFile) {
      setProgramDetailsFileError("Please choose a .html or .htm file.");
      return;
    }

    if (file.size > MAX_PROGRAM_DETAILS_FILE_BYTES) {
      setProgramDetailsFileError("HTML file must be 2MB or smaller.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      // Batched so the mode flips to "html" in the same render as the
      // content update - otherwise ReactQuill (still mounted in "visual"
      // mode for one extra render) receives the raw HTML as its `value`,
      // silently sanitizes it (stripping <style>/<script>/attributes), and
      // fires onChange with the mangled result before the mode switch lands.
      unstable_batchedUpdates(() => {
        handleProgramDetailsChange(text);
        setProgramDetailsMode("html");
        setProgramDetailsFileError("");
      });
    };
    reader.onerror = () => {
      setProgramDetailsFileError("Unable to read the selected file.");
    };
    reader.readAsText(file);
  };

  const submitModal = async (event) => {
    event.preventDefault();
    setModalState((prev) => ({ ...prev, saving: true, error: "" }));

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      if (!modalState.form.universityName.trim()) {
        throw new Error("University name is required.");
      }
      if (!modalState.form.programName.trim()) {
        throw new Error("Program name is required.");
      }

      const payload = {
        sourceId: modalState.form.sourceId.trim(),
        universityName: modalState.form.universityName.trim(),
        programName: modalState.form.programName.trim(),
        programDetails: modalState.form.programDetails.trim(),
        levelOfStudy: modalState.form.levelOfStudy.trim(),
        location: modalState.form.location.trim(),
        campus: modalState.form.campus.trim(),
        privateOrPublic: modalState.form.privateOrPublic.trim(),
        portalOrDirect: modalState.form.portalOrDirect.trim(),
        duration: modalState.form.duration.trim(),
        currency: modalState.form.currency.trim(),
        tuitionFeePerYear: parseNumberInput(modalState.form.tuitionFeePerYear),
        tuitionFeePerSemester: parseNumberInput(
          modalState.form.tuitionFeePerSemester
        ),
        tuitionFeePerCourse: parseNumberInput(
          modalState.form.tuitionFeePerCourse
        ),
        applicationFee: parseNumberInput(modalState.form.applicationFee),
        admissionFee: parseNumberInput(modalState.form.admissionFee),
        credits: parseNumberInput(modalState.form.credits),
        perCreditRate: parseNumberInput(modalState.form.perCreditRate),
        gpaOutOf4: parseNumberInput(modalState.form.gpaOutOf4),
        scholarship: modalState.form.scholarship.trim(),
        concentration: modalState.form.concentration.trim(),
        standardizedTests: modalState.form.standardizedTests.trim(),
        ielts: parseNumberInput(modalState.form.ielts),
        ieltsNblt: parseNumberInput(modalState.form.ieltsNblt),
        toefl: parseNumberInput(modalState.form.toefl),
        toeflNblt: parseNumberInput(modalState.form.toeflNblt),
        duolingo: parseNumberInput(modalState.form.duolingo),
        duolingoNblt: parseNumberInput(modalState.form.duolingoNblt),
        pte: parseNumberInput(modalState.form.pte),
        pteNblt: parseNumberInput(modalState.form.pteNblt),
        backlogs: modalState.form.backlogs.trim(),
        fifteenYearDegreeAccepted: parseBooleanInput(
          modalState.form.fifteenYearDegreeAccepted
        ),
        moiAccepted: parseBooleanInput(modalState.form.moiAccepted),
        wesRequired: parseBooleanInput(modalState.form.wesRequired),
        intakes: modalState.form.intakes.trim(),
        deadlines: modalState.form.deadlines.trim(),
      };

      const requestOptions = {
        method: modalState.mode === "add" ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      };

      const endpoint =
        modalState.mode === "add"
          ? ADMIN_COURSES_ENDPOINT
          : ADMIN_COURSES_UPDATE_ENDPOINT(modalState.course._id);

      const response = await fetch(endpoint, requestOptions);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Unable to save course.");
      }

      closeModal();
      fetchCourses();
    } catch (err) {
      setModalState((prev) => ({
        ...prev,
        error: err.message || "Unable to save course.",
      }));
    } finally {
      setModalState((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this course? This action cannot be undone.")) {
      return;
    }
    setDeletingId(id);
    setError("");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(ADMIN_COURSES_UPDATE_ENDPOINT(id), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to delete course.");
      }

      fetchCourses();
    } catch (err) {
      setError(err.message || "Unable to delete course.");
    } finally {
      setDeletingId("");
    }
  };

  const applyCourseTuitionLabel = getTuitionLabel(applyModal.course);

  return (
    <Styles>
      <CourseModalStyles />
      <div className="main-wrapper admin-courses-page">
        <HeaderTwo />
        <BreadcrumbBox title="Manage Courses" />

        <section className="courses-area">
          <Container>
            <Row>
              <Col md="12">
                {!authLoading && !isAdmin && (
                  <Alert variant="danger">
                    You do not have permission to view this page.
                  </Alert>
                )}
                {isAdmin && (
                  <div className="card-box">
                    <div className="actions">
                      <Button onClick={() => openModal("add")}>
                        Add Course
                      </Button>
                      <form
                        onSubmit={handleSearchSubmit}
                        className="search-form"
                      >
                        <input
                          type="text"
                          placeholder="Search by program, university, or location"
                          value={searchInput}
                          onChange={(event) =>
                            setSearchInput(event.target.value)
                          }
                          disabled={loading}
                        />
                        <button type="submit" disabled={loading}>
                          Search
                        </button>
                        <button
                          type="button"
                          className="clear-btn"
                          onClick={handleClearSearch}
                          disabled={
                            loading || (!searchTerm && !searchInput.trim())
                          }
                        >
                          Clear
                        </button>
                      </form>
                      <div className="bulk-actions">
                        <button
                          type="button"
                          className="export-btn"
                          onClick={handleExport}
                          disabled={exporting || loading}
                        >
                          {exporting ? "Exporting…" : "Export CSV"}
                        </button>
                        <button
                          type="button"
                          className="import-btn"
                          onClick={handleImportClick}
                          disabled={importStatus.processing || loading}
                        >
                          {importStatus.processing
                            ? "Importing…"
                            : "Import CSV"}
                        </button>
                        <input
                          ref={importFileInputRef}
                          type="file"
                          accept=".csv,text/csv"
                          className="file-input-hidden"
                          onChange={handleImportFileChange}
                          aria-hidden="true"
                        />
                      </div>
                      <div className="filters-panel">
                        <div className="filters-toolbar">
                          <span className="filters-title">Filters</span>
                          <button
                            type="button"
                            className="add-filter-btn"
                            onClick={addFilterRow}
                            disabled={loading || !filterOptionsAvailable}
                          >
                            Add Filter
                          </button>
                        </div>
                        <div className="filters-body">
                          {!filterOptionsAvailable ? (
                            <div className="no-filters-available">
                              No filterable fields available.
                            </div>
                          ) : (
                            filtersState.map((filter) => {
                              const fieldMeta = findFilterFieldMeta(
                                filter.field
                              );
                              const operatorOptions = fieldMeta
                                ? getOperatorsForType(fieldMeta.type)
                                : filterOperators;
                              const valueInputType =
                                fieldMeta?.type === "number"
                                  ? "number"
                                  : "text";
                              const isBooleanField =
                                fieldMeta?.type === "boolean";
                              const valueInputValue = isBooleanField
                                ? filter.value === true
                                  ? "true"
                                  : filter.value === false
                                  ? "false"
                                  : String(filter.value || "")
                                : typeof filter.value === "string"
                                ? filter.value
                                : filter.value ?? "";
                              return (
                                <div className="filter-row" key={filter.id}>
                                  <select
                                    value={filter.field}
                                    onChange={(event) =>
                                      handleFilterFieldChange(
                                        filter.id,
                                        event.target.value
                                      )
                                    }
                                    disabled={loading}
                                  >
                                    {filterFieldOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                  <select
                                    value={filter.operator}
                                    onChange={(event) =>
                                      handleFilterOperatorChange(
                                        filter.id,
                                        event.target.value
                                      )
                                    }
                                    disabled={
                                      loading || operatorOptions.length === 0
                                    }
                                  >
                                    {operatorOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                  {isBooleanField ? (
                                    <select
                                      value={valueInputValue}
                                      onChange={(event) =>
                                        handleFilterValueChange(
                                          filter.id,
                                          event.target.value
                                        )
                                      }
                                      disabled={loading}
                                    >
                                      <option value="">Select value</option>
                                      <option value="true">True</option>
                                      <option value="false">False</option>
                                    </select>
                                  ) : (
                                    <input
                                      type={valueInputType}
                                      value={
                                        valueInputType === "number"
                                          ? valueInputValue === null ||
                                            valueInputValue === undefined
                                            ? ""
                                            : valueInputValue
                                          : valueInputValue
                                      }
                                      onChange={(event) =>
                                        handleFilterValueChange(
                                          filter.id,
                                          event.target.value
                                        )
                                      }
                                      placeholder="Value"
                                      disabled={loading}
                                    />
                                  )}
                                  <button
                                    type="button"
                                    className="remove-filter-btn"
                                    onClick={() => removeFilterRow(filter.id)}
                                    disabled={loading}
                                    aria-label="Remove filter"
                                  >
                                    Remove
                                  </button>
                                </div>
                              );
                            })
                          )}
                        </div>
                        <div className="filters-footer">
                          <div className="filters-summary">
                            {hasAppliedFilters
                              ? `${appliedFilters.length} filter${
                                  appliedFilters.length === 1 ? "" : "s"
                                } applied`
                              : "No filters applied"}
                          </div>
                          <div className="filters-buttons">
                            <button
                              type="button"
                              className="apply-filters-btn"
                              onClick={applyFiltersFromState}
                              disabled={loading || !filtersChanged}
                            >
                              {hasAppliedFilters
                                ? "Update Filters"
                                : "Apply Filters"}
                            </button>
                            <button
                              type="button"
                              className="clear-filters-btn"
                              onClick={clearFilters}
                              disabled={loading}
                            >
                              Clear Filters
                            </button>
                          </div>
                        </div>
                      </div>
                      {(importStatus.error || importStatus.success) && (
                        <div className="import-status">
                          {importStatus.error && (
                            <Alert variant="danger" className="mb-0">
                              <div>{importStatus.error}</div>
                              {importStatus.errors.length > 0 && (
                                <ul className="mb-0 mt-2">
                                  {importStatus.errors
                                    .slice(0, 5)
                                    .map((issue, index) => (
                                      <li key={`import-error-${index}`}>
                                        {issue.row ? `Row ${issue.row}: ` : ""}
                                        {issue.message ||
                                          "Issue encountered during import."}
                                      </li>
                                    ))}
                                  {importStatus.errors.length > 5 && (
                                    <li>
                                      …and {importStatus.errors.length - 5} more
                                      issue(s)
                                    </li>
                                  )}
                                </ul>
                              )}
                            </Alert>
                          )}
                          {importStatus.success && (
                            <Alert
                              variant={
                                importStatus.errors.length
                                  ? "warning"
                                  : "success"
                              }
                              className="mb-0"
                            >
                              <div>{importStatus.success}</div>
                              {importStatus.errors.length > 0 && (
                                <ul className="mb-0 mt-2">
                                  {importStatus.errors
                                    .slice(0, 5)
                                    .map((issue, index) => (
                                      <li key={`import-warning-${index}`}>
                                        {issue.row ? `Row ${issue.row}: ` : ""}
                                        {issue.message ||
                                          "Issue encountered during import."}
                                      </li>
                                    ))}
                                  {importStatus.errors.length > 5 && (
                                    <li>
                                      …and {importStatus.errors.length - 5} more
                                      issue(s)
                                    </li>
                                  )}
                                </ul>
                              )}
                            </Alert>
                          )}
                        </div>
                      )}
                    </div>
                    {error && (
                      <Alert variant="danger" className="mt-3">
                        {error}
                      </Alert>
                    )}
                    {loading ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status" />
                      </div>
                    ) : (
                      <div className="data-grid">
                        {tableRows.length === 0 ? (
                          <Alert variant="info" className="mb-0">
                            No courses found.
                          </Alert>
                        ) : (
                          <>
                            <Table responsive bordered hover>
                              <thead>
                                <tr>
                                  <th>Program</th>
                                  <th>University</th>
                                  <th>Level</th>
                                  <th>Location</th>
                                  <th>Tuition</th>
                                  <th>Updated</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tableRows.map((row) => (
                                  <tr key={row.id}>
                                    <td>{row.programName}</td>
                                    <td>{row.universityName}</td>
                                    <td>{row.levelOfStudy || "—"}</td>
                                    <td>{row.location || "—"}</td>
                                    <td>{row.tuitionLabel}</td>
                                    <td>{row.updatedAt}</td>
                                    <td className="actions-cell">
                                      <button
                                        type="button"
                                        className="link-button"
                                        onClick={() =>
                                          openModal("edit", row.raw)
                                        }
                                      >
                                        Edit
                                      </button>
                                      <span className="divider">|</span>
                                      <button
                                        type="button"
                                        className="link-button apply"
                                        onClick={() => openApplyModal(row.raw)}
                                      >
                                        Apply
                                      </button>
                                      <span className="divider">|</span>
                                      <Link
                                        to={`${
                                          process.env.PUBLIC_URL || ""
                                        }/courses/${row.id}`}
                                        className="link-button view"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        View
                                      </Link>
                                      <span className="divider">|</span>
                                      <button
                                        type="button"
                                        className="link-button delete"
                                        onClick={() => handleDelete(row.id)}
                                        disabled={deletingId === row.id}
                                      >
                                        {deletingId === row.id
                                          ? "Deleting…"
                                          : "Delete"}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                            <div className="pagination-controls">
                              <div className="page-info">
                                Page {pagination.page} of{" "}
                                {pagination.totalPages} ·{" "}
                                {pagination.totalItems} total
                              </div>
                              <div className="page-actions">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPage((prev) => Math.max(prev - 1, 1))
                                  }
                                  disabled={pagination.page <= 1 || loading}
                                >
                                  Previous
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPage((prev) =>
                                      Math.min(prev + 1, pagination.totalPages)
                                    )
                                  }
                                  disabled={
                                    pagination.page >= pagination.totalPages ||
                                    loading
                                  }
                                >
                                  Next
                                </button>
                                <select
                                  value={pageSize}
                                  onChange={(event) => {
                                    const newSize =
                                      Number(event.target.value) || 10;
                                    setPageSize(newSize);
                                    setPage(1);
                                  }}
                                  disabled={loading}
                                >
                                  {[10, 20, 50, 100].map((size) => (
                                    <option key={size} value={size}>
                                      {size} / page
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>

      <Modal
        show={modalState.show}
        onHide={closeModal}
        centered
        scrollable
        dialogClassName="admin-courses-modal"
        size="lg"
      >
        <Form onSubmit={submitModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalState.mode === "add" ? "Add Course" : "Edit Course"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="course-form-fields">
              {modalState.error && (
                <Alert variant="danger">{modalState.error}</Alert>
              )}
              <Row>
                <Col md="6">
                  <Form.Group controlId="course_university">
                    <Form.Label>University Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="universityName"
                      value={modalState.form.universityName}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md="6">
                  <Form.Group controlId="course_program">
                    <Form.Label>Program Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="programName"
                      value={modalState.form.programName}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group controlId="course_program_details" className="mt-3">
                <div className="program-details-heading">
                  <Form.Label>Program Details</Form.Label>
                  <div className="program-details-heading-actions">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleProgramDetailsFileButtonClick}
                      disabled={modalState.saving}
                    >
                      Upload HTML File
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleToggleProgramDetailsMode}
                      disabled={modalState.saving}
                    >
                      {programDetailsMode === "visual"
                        ? "Edit HTML"
                        : "Visual Editor"}
                    </Button>
                  </div>
                </div>
                <input
                  ref={programDetailsFileInputRef}
                  type="file"
                  accept=".html,.htm,text/html"
                  className="file-input-hidden"
                  onChange={handleProgramDetailsFileChange}
                  tabIndex={-1}
                />
                {programDetailsFileError && (
                  <Alert
                    variant="danger"
                    className="program-details-file-error"
                    onClose={() => setProgramDetailsFileError("")}
                    dismissible
                  >
                    {programDetailsFileError}
                  </Alert>
                )}
                {programDetailsMode === "visual" ? (
                  <ReactQuill
                    className="program-details-editor"
                    theme="snow"
                    value={modalState.form.programDetails}
                    onChange={handleProgramDetailsChange}
                    readOnly={modalState.saving}
                  />
                ) : (
                  <Form.Control
                    className="program-details-html-editor"
                    as="textarea"
                    name="programDetails"
                    value={modalState.form.programDetails}
                    onChange={handleFormChange}
                    disabled={modalState.saving}
                    spellCheck={false}
                    placeholder="<h2>Program heading</h2><p>Program details with your own CSS...</p>"
                  />
                )}
                <Form.Text muted>
                  Use the visual toolbar, switch to HTML to enter tags directly,
                  or upload a full .html file (inline &lt;style&gt; CSS and
                  &lt;script&gt; are supported). The preview below renders it the
                  same way visitors will see it, inside a sandboxed iframe.
                </Form.Text>
                {modalState.form.programDetails && (
                  <div className="program-details-preview">
                    <div className="program-details-preview-label">
                      Live Preview
                    </div>
                    <iframe
                      title="Program details preview"
                      className="program-details-preview-frame"
                      srcDoc={modalState.form.programDetails}
                      sandbox="allow-scripts"
                    />
                  </div>
                )}
              </Form.Group>
              <Row className="mt-3">
                <Col md="4">
                  <Form.Group controlId="course_level">
                    <Form.Label>Level of Study</Form.Label>
                    <Form.Control
                      type="text"
                      name="levelOfStudy"
                      value={modalState.form.levelOfStudy}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="Bachelor, Master, etc."
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_location">
                    <Form.Label>Location</Form.Label>
                    <Form.Control
                      type="text"
                      name="location"
                      value={modalState.form.location}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="City, Country"
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_campus">
                    <Form.Label>Campus</Form.Label>
                    <Form.Control
                      type="text"
                      name="campus"
                      value={modalState.form.campus}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="Campus location"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <Form.Group controlId="course_duration">
                    <Form.Label>Duration</Form.Label>
                    <Form.Control
                      type="text"
                      name="duration"
                      value={modalState.form.duration}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="E.g. 2 years"
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_currency">
                    <Form.Label>Currency</Form.Label>
                    <Form.Control
                      type="text"
                      name="currency"
                      value={modalState.form.currency}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="USD, CAD, etc."
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_fee_year">
                    <Form.Label>Tuition / Year</Form.Label>
                    <Form.Control
                      type="number"
                      name="tuitionFeePerYear"
                      value={modalState.form.tuitionFeePerYear}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      min="0"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <Form.Group controlId="course_fee_sem">
                    <Form.Label>Tuition / Semester</Form.Label>
                    <Form.Control
                      type="number"
                      name="tuitionFeePerSemester"
                      value={modalState.form.tuitionFeePerSemester}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_fee_course">
                    <Form.Label>Tuition / Course</Form.Label>
                    <Form.Control
                      type="number"
                      name="tuitionFeePerCourse"
                      value={modalState.form.tuitionFeePerCourse}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_application_fee">
                    <Form.Label>Application Fee</Form.Label>
                    <Form.Control
                      type="number"
                      name="applicationFee"
                      value={modalState.form.applicationFee}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      min="0"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <Form.Group controlId="course_admission_fee">
                    <Form.Label>Admission Fee</Form.Label>
                    <Form.Control
                      type="number"
                      name="admissionFee"
                      value={modalState.form.admissionFee}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_credits">
                    <Form.Label>Credits</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.5"
                      name="credits"
                      value={modalState.form.credits}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_per_credit_rate">
                    <Form.Label>Per Credit Rate</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="perCreditRate"
                      value={modalState.form.perCreditRate}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      min="0"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <Form.Group controlId="course_gpa">
                    <Form.Label>GPA (out of 4)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      max="4"
                      name="gpaOutOf4"
                      value={modalState.form.gpaOutOf4}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="E.g. 3.2"
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_scholarship">
                    <Form.Label>Scholarship</Form.Label>
                    <Form.Control
                      type="text"
                      name="scholarship"
                      value={modalState.form.scholarship}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="Scholarship information"
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_concentration">
                    <Form.Label>Concentration</Form.Label>
                    <Form.Control
                      type="text"
                      name="concentration"
                      value={modalState.form.concentration}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="E.g. Computer Science, Finance"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <Form.Group controlId="course_backlogs">
                    <Form.Label>Backlogs</Form.Label>
                    <Form.Control
                      type="text"
                      name="backlogs"
                      value={modalState.form.backlogs}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="Allowed or expected backlogs"
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_intakes">
                    <Form.Label>Intakes</Form.Label>
                    <Form.Control
                      type="text"
                      name="intakes"
                      value={modalState.form.intakes}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="Fall, Spring, etc."
                    />
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_deadlines">
                    <Form.Label>Deadlines</Form.Label>
                    <Form.Control
                      type="text"
                      name="deadlines"
                      value={modalState.form.deadlines}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                      placeholder="Application deadlines"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <Form.Group controlId="course_fifteen_year">
                    <Form.Label>15 Year Degree Accepted</Form.Label>
                    <Form.Control
                      as="select"
                      name="fifteenYearDegreeAccepted"
                      value={modalState.form.fifteenYearDegreeAccepted}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                    >
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_moi_accepted">
                    <Form.Label>MOI Accepted</Form.Label>
                    <Form.Control
                      as="select"
                      name="moiAccepted"
                      value={modalState.form.moiAccepted}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                    >
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group controlId="course_wes_required">
                    <Form.Label>WES Required</Form.Label>
                    <Form.Control
                      as="select"
                      name="wesRequired"
                      value={modalState.form.wesRequired}
                      onChange={handleFormChange}
                      disabled={modalState.saving}
                    >
                      <option value="">Unknown</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={modalState.saving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={modalState.saving}
            >
              {modalState.saving ? "Saving..." : "Save Course"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={applyModal.show}
        onHide={closeApplyModal}
        centered
        dialogClassName="admin-courses-apply-modal"
        size="lg"
      >
        <Form onSubmit={handleCreateApplication}>
          <Modal.Header closeButton>
            <Modal.Title>
              {applyModal.course?.programName
                ? `Apply: ${applyModal.course.programName}`
                : "Create Application"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {applyModal.error && (
              <Alert variant="danger">{applyModal.error}</Alert>
            )}
            {applyModal.success && (
              <Alert variant="success">{applyModal.success}</Alert>
            )}

            {applyModal.course && (
              <div className="course-summary">
                <h5>{applyModal.course.programName || "Untitled Program"}</h5>
                <p className="university">
                  {applyModal.course.universityName || "University"}
                </p>
                <ul className="meta list-unstyled">
                  {applyModal.course.location && (
                    <li>
                      <strong>Location:</strong> {applyModal.course.location}
                    </li>
                  )}
                  {applyModal.course.levelOfStudy && (
                    <li>
                      <strong>Level:</strong> {applyModal.course.levelOfStudy}
                    </li>
                  )}
                  {applyModal.course.duration && (
                    <li>
                      <strong>Duration:</strong> {applyModal.course.duration}
                    </li>
                  )}
                  {applyModal.course.currency &&
                    (applyModal.course.tuitionFeePerYear ||
                      applyModal.course.tuitionFeePerSemester ||
                      applyModal.course.tuitionFeePerCourse) && (
                      <li>
                        <strong>Tuition:</strong> {applyModal.course.currency}{" "}
                        {Number(
                          applyModal.course.tuitionFeePerYear ??
                            applyModal.course.tuitionFeePerSemester ??
                            applyModal.course.tuitionFeePerCourse
                        ).toLocaleString()}
                      </li>
                    )}
                </ul>
              </div>
            )}

            <div className="user-picker">
              <Form.Label>Select Applicant</Form.Label>
              <div className="user-search">
                <Form.Control
                  type="text"
                  placeholder="Search users by name or email"
                  value={applyModal.searchInput}
                  onChange={handleApplyUserSearchChange}
                  onKeyDown={handleApplyUserSearchKeyDown}
                  disabled={applyModal.loadingUsers || applyModal.submitting}
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={handleApplyUserSearchSubmit}
                  disabled={applyModal.loadingUsers || applyModal.submitting}
                >
                  Search
                </Button>
              </div>
              {applyModal.loadingUsers ? (
                <div className="loading-users">
                  <Spinner animation="border" role="status" size="sm" />
                </div>
              ) : (
                <Form.Control
                  as="select"
                  className="user-select"
                  value={applyModal.selectedUserId}
                  onChange={handleApplyUserSelect}
                  disabled={
                    applyModal.submitting || applyModal.users.length === 0
                  }
                >
                  <option value="">Select a user</option>
                  {applyModal.users.map((candidate) => (
                    <option key={getUserId(candidate)} value={getUserId(candidate)}>
                      {`${candidate.firstName || ""} ${
                        candidate.lastName || ""
                      }`.trim() || candidate.email}{" "}
                      ({candidate.email})
                    </option>
                  ))}
                </Form.Control>
              )}
              {!applyModal.loadingUsers && applyModal.users.length === 0 && (
                <small className="muted">
                  No users found. Try a different search.
                </small>
              )}
            </div>

            <div className="agent-picker mt-3">
              <Form.Label>Assign Agent (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search agents by name, email, or phone"
                value={applyModal.agentSearchInput}
                onChange={handleApplyAgentSearchChange}
                disabled={applyModal.loadingAgents || applyModal.submitting}
              />
              {applyModal.loadingAgents ? (
                <div className="loading-agents mt-2">
                  <Spinner animation="border" role="status" size="sm" />
                </div>
              ) : (
                <Form.Control
                  as="select"
                  className="agent-select mt-2"
                  value={applyModal.selectedAgentId}
                  onChange={handleApplyAgentSelect}
                  disabled={
                    applyModal.submitting || filteredAgents.length === 0
                  }
                >
                  <option value="">No agent</option>
                  {filteredAgents.map((agent) => (
                    <option key={getAgentId(agent)} value={getAgentId(agent)}>
                      {formatAgentLabel(agent)}
                    </option>
                  ))}
                </Form.Control>
              )}
              {!applyModal.loadingAgents && filteredAgents.length === 0 && (
                <small className="muted">
                  {applyModal.agentSearchInput.trim()
                    ? "No agents match your search."
                    : "No agents available. Add one from the Agents page."}
                </small>
              )}
            </div>

            <div className="intake-picker mt-3">
              <Form.Label>Select Intake</Form.Label>
              {applyModal.loadingIntakes ? (
                <div className="loading-intakes">
                  <Spinner animation="border" role="status" size="sm" />
                </div>
              ) : (
                <Form.Control
                  as="select"
                  className="intake-select"
                  value={applyModal.selectedIntakeId}
                  onChange={handleApplyIntakeSelect}
                  disabled={
                    applyModal.submitting || applyModal.intakes.length === 0
                  }
                  required
                >
                  <option value="">Select an intake</option>
                  {applyModal.intakes.map((intake) => (
                    <option key={getIntakeId(intake)} value={getIntakeId(intake)}>
                      {formatIntakeLabel(intake)}
                    </option>
                  ))}
                </Form.Control>
              )}
              {!applyModal.loadingIntakes && applyModal.intakes.length === 0 && (
                <small className="muted">
                  No intakes found. Create an intake before submitting an application.
                </small>
              )}
            </div>

            <Form.Group controlId="apply_message" className="mt-3">
              <Form.Label>Message (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add a note for the applicant"
                value={applyModal.message}
                onChange={handleApplyMessageChange}
                disabled={applyModal.submitting}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeApplyModal}
              disabled={applyModal.submitting}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              type="submit"
              disabled={
                applyModal.submitting ||
                !applyModal.selectedUserId ||
                !applyModal.selectedIntakeId
              }
            >
              {applyModal.submitting ? "Creating..." : "Create Application"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Styles>
  );
};

export default AdminCourses;
